import { z } from "zod";
import { ChatInCollectionSchema } from "@/schema/collections";
import { LibrarySchema } from "@/schema/library";
import { SegmentedImageEditorSchema } from "@/schema/viewport";

export const ChatVisibilitySchema = z.enum(["private", "public", "restricted"]);

export const ChatEditorItemSchema = z.object({
  current: SegmentedImageEditorSchema.nullish(),
  original: z.any().nullish(),
});

export const ChatEditorSchema = z.record(z.string(), ChatEditorItemSchema);

export const ChatReasoningSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  content: z.any().nullish(),
  artifact: z.any().nullish(),
  streaming: z.boolean().nullish(),
  created_at: z.string().nullish(),
});

export const ChatMessageContentSchema = z.object({
  id: z.string(),
  content: z.string(),
  streaming: z.boolean().nullish(),
  created_at: z.string().nullish(),
});

export const ChatArtifactSchema = z.object({
  type: z.string(),
  content: z.any().nullish(),
});

export const ChatMessageSchema = z.object({
  id: z.string(),
  type: z.enum(["user", "ai"]),
  artifact: z.any().nullish(),
  streaming: z.boolean().nullish(),
  reasoning: z.array(ChatReasoningSchema).nullish(),
  message: z.array(ChatMessageContentSchema).or(z.string()),
});

export const ChatInviteSchema = z.object({
  id: z.string(),
  chat_id: z.string(),
  email: z.string(),
  invited_by: z.string(),
  deleted_by: z.string().nullish(),
  invited_at: z.string(),
  deleted_at: z.string().nullish(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const ChatSessionSchema = z.object({
  id: z.string(),
  title: z.string(),
  agent_name: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  stream_id: z.string().nullish(),
  choices: z.array(z.string()).nullish(),
  messages: z.array(ChatMessageSchema).nullish(),
  settings: z.record(z.string(), z.any()).nullish(),
  editor: ChatEditorSchema.nullish(),
  ratings: z.record(z.string(), z.number()).nullish(),
  invites: z.array(ChatInviteSchema).nullish(),
  visibility: ChatVisibilitySchema.nullish(),
  chat_collections: z.array(ChatInCollectionSchema).nullish(),
  latest_creation: LibrarySchema.nullish(),
});

export type ChatSession = z.infer<typeof ChatSessionSchema>;
export type ChatEditor = z.infer<typeof ChatEditorSchema>;
export type ChatEditorItem = z.infer<typeof ChatEditorItemSchema>;
export type ChatReasoning = z.infer<typeof ChatReasoningSchema>;
export type ChatMessage = z.infer<typeof ChatMessageSchema>;
export type ChatInvite = z.infer<typeof ChatInviteSchema>;
export type ChatVisibility = z.infer<typeof ChatVisibilitySchema>;
